'use client';

import { useTranslations } from 'next-intl';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { Link } from '@/i18n/routing';
import {
  neighborhoodSlugsForServicePage,
  sofiaServiceAreaPath,
  type SofiaServicePagePath,
} from '@/lib/seo/sofia-service-areas';
import { cn } from '@/lib/utils';

export interface ServiceNeighborhoodLinksProps {
  servicePath: SofiaServicePagePath;
  className?: string;
}

export function ServiceNeighborhoodLinks({
  servicePath,
  className,
}: ServiceNeighborhoodLinksProps) {
  const t = useTranslations('serviceAreas');
  const slugs = neighborhoodSlugsForServicePage(servicePath);

  if (slugs.length === 0) {
    return null;
  }

  return (
    <section
      className={cn('border-t border-oxford-blue/10 bg-white py-12 md:py-16', className)}
      aria-labelledby='service-neighborhood-links-heading'
    >
      <UnifiedContainer size='xl' padding='lg'>
        <h2
          id='service-neighborhood-links-heading'
          className='text-2xl font-extrabold tracking-tight text-oxford-blue md:text-3xl'
        >
          {t('servicePageLinks.title')}
        </h2>
        <nav aria-label={t('servicePageLinks.title')} className='mt-6'>
          <ul className='flex flex-wrap gap-2.5'>
            {slugs.map(slug => (
              <li key={slug}>
                <Link
                  href={sofiaServiceAreaPath(slug)}
                  className='inline-flex min-h-11 items-center rounded-lg border border-oxford-blue/15 bg-white px-3.5 py-2 text-sm font-medium text-oxford-blue-700 transition-colors hover:border-orange-peel/40 hover:bg-orange-peel/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel'
                >
                  {t(`areas.${slug}.name`)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className='mt-6'>
          <Link
            href='/service-areas'
            className='text-sm font-semibold text-oxford-blue underline-offset-4 hover:text-orange-peel hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel'
          >
            {t('servicePageLinks.viewAll')}
          </Link>
        </div>
      </UnifiedContainer>
    </section>
  );
}
